import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import clsx from "clsx";
import { Button, Skeleton, Space } from "antd-mobile";
import { useTranslation } from "react-i18next";
import { openTelegramLink } from "@telegram-apps/sdk-react";
import { config } from "~/config";
import {
    formatBalance,
    formatPercent,
    formatPrice,
    formatValue,
} from "~/utils/number-format";
import { useUXUYWallet } from "~/Provider/UXUYWallet";
import { fetchWalletBalance } from "~/api/endpoints";
import {
    IconArrowDown,
    IconArrowUp,
    IconChainAll,
    IconDownFill,
    IconWallet,
} from "~/components/Iconfont/Iconfont";
import { skdUtils } from "~/utils/tgLink";
import { Chain, TokenWithBalance } from "~/types";
import { BaseModal } from "~/components/widget/BaseModal/BaseModal";
import { SafeAreaBottom } from "~/components/widget/SafeAreaBottom/SafeAreaBottom";
import { TokenIconSize } from "~/components/TokenIcon";
import { TokenBadge } from "~/components/TokenBadge/TokenBadge";
import TokenList from "~/components/TokenList/TokenList";
import { useNavigate } from "react-router";
import { Page } from "~/components/Page/Page";
import { sleep } from "~/tools/common";
import { getMaximiumnPopupHeight } from "~/components/widget/SafeAreaTop/SafeAreaTop";

const WalletAssets = () => {
    const { t } = useTranslation();
    const {
        supportedChains,
        isConnectedTo,
        disconnect,
        getAddress,
        getAddressType,
    } = useUXUYWallet();

    const navigate = useNavigate();

    const addresses = supportedChains
        .filter((chainId) => isConnectedTo(chainId))
        .map((chainId) => {
            const address = getAddress(chainId) as string;
            const addressType = getAddressType(chainId) as number;
            return { address, address_type: addressType };
        });

    const queryWalletBalance = useCallback((args: any) => {
        const [, open, addresses] = args;
        if (!open) return null;

        if (addresses.length === 0)
            return { value: "0", change: "0", list: [] };

        return fetchWalletBalance(addresses).then((res) => res.data);
    }, []);

    const initialData = useMemo(() => {
        const cachedAssets = window.localStorage.getItem(
            "__uxuy_dex_cached_wallet_assets__"
        );

        if (cachedAssets) {
            try {
                return JSON.parse(cachedAssets);
            } catch {
                return null;
            }
        }

        return null;
    }, []);

    const { data, mutate } = useSWR(
        ["WalletConnect", open, addresses],
        queryWalletBalance,
        {
            fallbackData: initialData,
            onSuccess(data) {
                window.localStorage.setItem(
                    "__uxuy_dex_cached_wallet_assets__",
                    JSON.stringify(data)
                );
            },
        }
    );

    const [walletChains, setWalletChains] = useState<Chain[]>([]);
    const [selectedChain, setSelectedChain] = useState<Chain | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (data && data.list.length > 0) {
            const [, chainList] = data.list.reduce(
                (result, token) => {
                    const set = result[0];
                    const chain_id = String(token.token_base.chain_id);

                    if (set.has(chain_id)) {
                        return result;
                    }

                    set.add(chain_id);

                    result[1].push({
                        id: chain_id,
                        logo: token.token_base.chain_logo,
                        name: token.token_base.chain_name,
                    });

                    return result;
                },
                [new Set<string>(), new Array<Chain>()]
            );

            setWalletChains(chainList);
            const cachedSelectedChainId = window.localStorage.getItem(
                "__uxuy_cache_wallet_assets_selected_chain_id__"
            );

            const selectedChain = cachedSelectedChainId
                ? chainList.find(
                      (item) =>
                          String(item.id) === String(cachedSelectedChainId)
                  )
                : null;

            if (selectedChain) {
                setSelectedChain(selectedChain);
            }
        }
    }, [data]);

    const walletOperations = [
        {
            label: t("wallet_send"),
            icon: <IconArrowUp className="text-h5 text-text_brand" />,
            buildUrl() {
                const params = {
                    path: `/select-token`,
                    action: "send",
                    route_replace: "1",
                };

                return `${config.wallet_direct_link}?startapp=D_${skdUtils.encodeTelegramUrlParameters(params || {})}`;
            },
        },
        {
            label: t("wallet_receive"),
            icon: <IconArrowDown className="text-h5 text-text_brand" />,
            buildUrl() {
                const params = {
                    path: `/select-token-receive`,
                    action: "receive",
                    route_replace: "1",
                };

                return `${config.wallet_direct_link}?startapp=D_${skdUtils.encodeTelegramUrlParameters(params || {})}`;
            },
        },
        {
            label: t("tab_bar_balance"),
            icon: <IconWallet className="text-h5 text-text_brand" />,
            buildUrl() {
                return config.wallet_direct_link;
            },
        },
    ];

    const redirectToKDetail = (token: any) => {
        navigate(
            `/k-detail?chain_id=${token.token_base.chain_id}&contract=${token.token_base.contract}`
        );
    };

    const tokenColumns = [
        {
            key: "name",
            label: `${t("name")}`,
            value(token: TokenWithBalance) {
                return formatPrice(token.price);
            },
            change() {
                return "";
            },
        },
        {
            key: "price",
            label: `${t("price")} / ${t("changes")}`,
            value(token: TokenWithBalance) {
                return formatBalance(token.balance);
            },
            change(token: any) {
                return (
                    <span className="font-semibold text-direction1">
                        {formatValue(token.value)}
                    </span>
                );
            },
        },
    ];

    return (
        <Page
            onRefresh={async () => {
                mutate();
                await sleep(600);
            }}
        >
            <div className="bg-bg_0 min-h-screen py-4">
                <div className="px-4">
                    <div>
                        <div className="flex items-center justify-between">
                            <span className="text-body4 text-text_2">
                                {t("your_balance")}
                            </span>
                            <Button
                                color="primary"
                                fill="solid"
                                style={
                                    {
                                        height: 32,
                                        padding: "0 12px",
                                        "--border-radius": "20px",
                                        "--border-color": "transparent",
                                        "--text-color": "var(--color_text_1)",
                                        "--background-color":
                                            "var(--color_bg_1)",
                                    } as React.CSSProperties
                                }
                                onClick={disconnect}
                            >
                                <span className="text-body4 font-semibold">
                                    {t("disconnect")}
                                </span>
                            </Button>
                        </div>
                        {!data ? (
                            <div className="flex items-start">
                                <Skeleton className="text-h1 text-text_1 w-[100px] h-[36px] rounded-[4px] mr-2"></Skeleton>
                                <Skeleton className="w-[50px] h-[20px] rounded-[4px]" />
                            </div>
                        ) : (
                            <div className="flex items-start">
                                <span className="text-h1 text-text_1 font-semibold mr-2 !leading-[1]">
                                    {formatValue(data?.value)}
                                </span>
                                <span
                                    className={clsx(
                                        "font-semibold text-body3 mt-0.5",
                                        (Number(data?.change) as number) == 0
                                            ? "!text-text_3"
                                            : (Number(
                                                    data?.change
                                                ) as number) >= 0
                                              ? "!text-text_buy"
                                              : "!text-text_sell"
                                    )}
                                >
                                    {formatPercent(
                                        Number(data?.change) as number
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-around px-4 space-x-2 my-5">
                    {walletOperations.map((item, index) => {
                        return (
                            <Button
                                key={index}
                                className="flex-1 "
                                color="primary"
                                fill="solid"
                                style={
                                    {
                                        height: 64,
                                        padding: "12px",
                                        "--border-radius": "12px",
                                        "--border-color":
                                            "var(--color_border_1)",
                                        "--text-color": "var(--color_text_1)",
                                        "--background-color":
                                            "var(--color_bg_0)",
                                    } as React.CSSProperties
                                }
                                onClick={() => {
                                    openTelegramLink(item.buildUrl());
                                }}
                            >
                                <div className="flex flex-col justify-center items-center">
                                    <span className="">{item.icon}</span>
                                    <span className="text-text_1 text-body4">
                                        {item.label}
                                    </span>
                                </div>
                            </Button>
                        );
                    })}
                </div>
                <div className="h-11 px-4 flex items-center justify-between">
                    <span className="text-text_1 text-body1 font-semibold">
                        {t("my_assets")}
                    </span>
                    <div className="text-text_1 text-body4 bg-bg_1 rounded-[4px] flex items-center">
                        <button
                            className="flex items-center px-1 py-1"
                            onClick={() => setVisible(!visible)}
                        >
                            {selectedChain ? (
                                <span className="w-[18px] h-[18px]">
                                    <TokenBadge
                                        size={TokenIconSize.mini}
                                        icon={selectedChain.logo}
                                        symbol={selectedChain.name}
                                    />
                                </span>
                            ) : (
                                <span className="w-[18px] h-[18px] bg-text_1 rounded-full flex items-center justify-center">
                                    <IconChainAll className="text-bg_0 text-[12px]" />
                                </span>
                            )}
                            <span className="font-Regular ml-1">
                                {selectedChain
                                    ? selectedChain.name
                                    : t("all_chains")}
                            </span>
                            <IconDownFill className="ml-1" />
                        </button>
                        <BaseModal
                            className="flex flex-col bg-bg_bottom z-[999]"
                            style={{
                                height: getMaximiumnPopupHeight(),
                            }}
                            titleType="title"
                            title={t("switch_network")}
                            open={visible}
                            modal={true}
                            onclickHeaderClose={() => setVisible(false)}
                            dismissible={true}
                            onOpenChange={(bool: boolean) => setVisible(bool)}
                        >
                            <div
                                className="flex relative w-full"
                                style={{ height: "calc(95vh - 3.75rem)" }}
                            >
                                <div className="flex-auto relative">
                                    <div className="absolute top-0 right-0 bottom-0 left-0 px-4 overflow-auto">
                                        <Space
                                            className="w-full mt-1"
                                            direction="vertical"
                                            style={{ "--gap": "6px" }}
                                        >
                                            <div
                                                key={"all"}
                                                className={clsx(
                                                    "flex items-center bg-bg_0 rounded-xl p-3 cursor-pointer",
                                                    selectedChain
                                                        ? ""
                                                        : "outline outline-[2px] outline-text_brand"
                                                )}
                                                onClick={() => {
                                                    setSelectedChain(null);
                                                    setVisible(false);
                                                    window.localStorage.removeItem(
                                                        "__uxuy_cache_wallet_assets_selected_chain_id__"
                                                    );
                                                }}
                                            >
                                                <span className=" w-[44px] h-[44px] bg-text_1 rounded-full flex items-center justify-center">
                                                    <IconChainAll className="text-bg_0 text-[28px]" />
                                                </span>
                                                <span className="text-text_1 text-body1 font-semibold ml-3">
                                                    {t("all_chains")}
                                                </span>
                                            </div>
                                            {walletChains.map((chain) => {
                                                return (
                                                    <div
                                                        key={chain.id}
                                                        className={clsx(
                                                            "flex items-center bg-bg_0 rounded-xl p-3 cursor-pointer",
                                                            selectedChain?.id ==
                                                                chain.id &&
                                                                "outline outline-[2px] outline-text_brand"
                                                        )}
                                                        onClick={() => {
                                                            setSelectedChain(
                                                                chain
                                                            );
                                                            setVisible(false);
                                                            window.localStorage.setItem(
                                                                "__uxuy_cache_wallet_assets_selected_chain_id__",
                                                                chain.id
                                                            );
                                                        }}
                                                    >
                                                        <span>
                                                            <TokenBadge
                                                                size={
                                                                    TokenIconSize.small
                                                                }
                                                                tailIconSize={
                                                                    TokenIconSize.mini21
                                                                }
                                                                icon={
                                                                    chain.logo
                                                                }
                                                                symbol={
                                                                    chain.name
                                                                }
                                                            />
                                                        </span>
                                                        <span className="text-text_1 text-body1 font-semibold ml-3">
                                                            {chain.name}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </Space>
                                        <div className="h-20"></div>
                                        <SafeAreaBottom />
                                    </div>
                                </div>
                            </div>
                        </BaseModal>
                    </div>
                </div>
                <TokenList
                    isLoading={!data}
                    tokens={
                        (data?.list ?? []).filter((token) =>
                            selectedChain
                                ? String(token.token_base.chain_id) ===
                                  String(selectedChain.id)
                                : true
                        ) as any
                    }
                    columns={tokenColumns as any}
                    showHead={false}
                    layout="Page"
                    onTokenClick={redirectToKDetail}
                />
            </div>
        </Page>
    );
};

export default WalletAssets;
